import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  useTheme,
  alpha,
  Divider,
  CircularProgress
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  ArrowForward as NextIcon,
  History as HistoryIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuthTokenCheck } from '../../hooks/useAuthTokenCheck';
import { useWorkflowContext } from '../../context/WorkflowContext';
import { useToast } from '../../context/ToastContext';
import config from '../../utils/config';
import InputFormContainerV2 from '../../components/workflow-v2/InputFormContainerV2';
import SessionExpiredDialog from '../../components/workflow-v2/SessionExpiredDialog';
import AIResponseStreaming from '../../components/workflow-v2/AIResponseStreaming';
import KeywordPreviewPanel from '../../components/workflow-v2/KeywordPreviewPanel';
import { workflowService } from '../../services/api/workflowService';

const KeywordAnalysisStep: React.FC = () => {
  const { clientId, workflowId } = useParams<{ clientId: string; workflowId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showToast } = useToast();
  const { workflow, moduleStatuses } = useWorkflowContext();
  
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [streamedContent, setStreamedContent] = useState<string>('');
  const [testKeywordLimit, setTestKeywordLimit] = useState<number | null>(null);
  const [fileUploadCompleted, setFileUploadCompleted] = useState<boolean | null>(null);
  const [isCheckingFileUpload, setIsCheckingFileUpload] = useState(true);
  const [isLoadingPreviousResult, setIsLoadingPreviousResult] = useState(true);
  const [mostRecentModuleIsFileUpload, setMostRecentModuleIsFileUpload] = useState(false);

  // Use auth token check hook
  const { validateToken, showSessionExpiredDialog, setShowSessionExpiredDialog } = useAuthTokenCheck();
  
  // Combined loading state - true when either check is in progress
  const isInitialLoading = isCheckingFileUpload || isLoadingPreviousResult;
  
  useEffect(() => {
    const checkFileUploadStatus = async () => {
      if (!workflowId) {
        setIsCheckingFileUpload(false);
        return;
      }
      
      try {
        setIsCheckingFileUpload(true);
        
        // Get workflow (includes modules in response)
        const dbWorkflow = await workflowService.getDbWorkflow(workflowId);
        const allModules = dbWorkflow.modules || [];
        
        // Find the most recent module (highest module_id)
        let mostRecentModule = null;
        let highestModuleId = -1;
        
        allModules.forEach((module: any) => {
          if (module.module_id && module.module_id > highestModuleId) {
            highestModuleId = module.module_id;
            mostRecentModule = module;
          }
        });
        
        console.log('[KeywordAnalysis] Most recent module:', mostRecentModule?.type || mostRecentModule?.module_type, 'ID:', mostRecentModule?.module_id);
        
        // Check if there's a completed file upload module
        // Handle both 'type' and 'module_type' field names
        const fileUploadModule = allModules.find((m: any) => (m.type || m.module_type) === 'fileupload_v2');
        const hasCompletedUpload = fileUploadModule?.status === 'completed';
        
        setFileUploadCompleted(hasCompletedUpload);
        
        // Simple logic: Is the most recent module fileupload_v2? (handle both field names)
        const moduleType = mostRecentModule?.type || mostRecentModule?.module_type;
        const isFileUploadMostRecent = moduleType === 'fileupload_v2';
        setMostRecentModuleIsFileUpload(isFileUploadMostRecent);
        
        console.log('[KeywordAnalysis] Most recent is file upload:', isFileUploadMostRecent);
      } catch (error) {
        console.error('Error checking file upload status:', error);
        setFileUploadCompleted(false);
        setMostRecentModuleIsFileUpload(false);
      } finally {
        setIsCheckingFileUpload(false);
      }
    };
    
    checkFileUploadStatus();
  }, [workflowId]);
   
  // Load previous analysis result if exists (only once on mount)
  useEffect(() => {
    const loadPreviousResult = async () => {
      if (!workflowId) {
        setIsLoadingPreviousResult(false);
        return;
      }
      
      try {
        const statusUrl = `${config.api.snowflake}/api/v1/workflow-v2/keyword-analysis/${workflowId}/status`;
        const response = await fetch(statusUrl);
        
        if (response.ok) {
          const data = await response.json();
          
          if (data.status === 'completed' && data.result) {
            setStreamedContent(data.result);
            // Only auto-show as complete - will be hidden by mostRecentModuleIsFileUpload check
            setAnalysisComplete(true);
            console.log('[KeywordAnalysis] Loaded previous analysis result');
          } else if (data.status === 'processing') {
            showToast('Previous analysis is still in progress', 'warning');
          } else if (data.status === 'failed') {
            showToast(`Previous analysis failed: ${data.error_message || 'Unknown error'}`, 'error');
          }
        }
      } catch (error) {
        console.error('Error loading previous result:', error);
        // Don't show toast for error - it's okay if there's no previous result
      } finally {
        setIsLoadingPreviousResult(false);
      }
    };
    
    loadPreviousResult();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]); // Only run when workflowId changes, not when showToast changes
  
  // Handle business setup submission from InputFormContainer
  const handleAnalysisSubmit = async (businessInfo: any) => {
    if (!clientId || !workflowId) {
      showToast('Missing client ID or workflow ID', 'error');
      return;
    }

    setIsAnalyzing(true);
    setStreamedContent('');
    setAnalysisComplete(false);
    
    // Track if this is a test run
    setTestKeywordLimit(businessInfo.keywordLimit || null);
    
    try {
      // Validate authentication token
      const { isValid, token } = validateToken();
      if (!isValid) {
        setIsAnalyzing(false);
        return;
      }

      // Call backend streaming endpoint using absolute URL
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/keyword-analysis/stream`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          business_name: businessInfo.businessName,
          brand_tokens: businessInfo.brandTokens,
          industry: businessInfo.industry,
          description: businessInfo.description,
          target_audience: businessInfo.targetAudience,
          additional_context: businessInfo.additionalContext,
          custom_prompt: businessInfo.customPrompt,
          keyword_limit: businessInfo.keywordLimit,
          model: businessInfo.llmModel
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      // Read the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Stream completed successfully
          setAnalysisComplete(true);
          // Analysis created a new module1_v2 record, so file upload is no longer the most recent
          setMostRecentModuleIsFileUpload(false);
          showToast('Query analysis completed successfully!', 'success');
          break;
        }

        // Decode chunk and append
        const chunk = decoder.decode(value, { stream: true });
        accumulatedContent += chunk;
        setStreamedContent(accumulatedContent);
      }
      
    } catch (error) {
      console.error('Error during analysis:', error);
      showToast('Failed to complete query analysis', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const handleProceedToNext = () => {
    navigate(`/workflow2/${clientId}/${workflowId}/semantic`);
  };
  
  return (
    <Box>
      <Typography variant="body1" color="text.secondary" paragraph>
      Configure the business you're analyzing. Some inputs will be used to generate or analyze results.
      </Typography>
      
      {/* Show warning if file upload is not completed */}
      {!isInitialLoading && fileUploadCompleted === false && (
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mb: 3,
            backgroundColor: alpha(theme.palette.warning.main, 0.08),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <WarningIcon sx={{ color: theme.palette.warning.main, mt: 0.5, fontSize: 40 }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                File Upload Not Completed
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                No files have been uploaded or the file upload process has not been completed yet. 
                Please go back to <strong>Step 1: File Upload</strong> and ensure all files are successfully uploaded before proceeding with query analysis.
              </Typography>
              <Button
                variant="contained"
                color="warning"
                onClick={() => navigate(`/workflow2/${clientId}/${workflowId}`)}
                sx={{ mt: 1 }}
              >
                Go to File Upload
              </Button>
            </Box>
          </Box>
        </Paper>
      )}
      
      {/* Loading spinner when checking for previous results or file upload status */}
      {isInitialLoading && (
        <Paper
          elevation={2}
          sx={{
            p: 4,
            mb: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="body1" color="text.secondary">
            Loading workflow data...
          </Typography>
        </Paper>
      )}
      
      {/* Configuration Section - Show when not analyzing and (not complete OR most recent is file upload) and file upload is done */}
      {!isAnalyzing && (!analysisComplete || mostRecentModuleIsFileUpload) && fileUploadCompleted && !isInitialLoading && (
        <Box>
          {/* If most recent module is file upload, always show the form (user needs to run analysis) */}
          {/* Otherwise, show button to view latest results if they exist */}
          {streamedContent && !mostRecentModuleIsFileUpload && (
            <Alert 
              severity="info" 
              sx={{ mb: 2 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  variant="outlined"
                  onClick={() => setAnalysisComplete(true)}
                  startIcon={<HistoryIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  View Latest Results
                </Button>
              }
            >
              <AlertTitle>Previous Analysis Available</AlertTitle>
              The last analysis completed successfully. You can view the results or start a new analysis below.
            </Alert>
          )}
          
          <Box sx={{ height: '1460px', display: 'flex', pb:2, gap: 3 }}>
            {/* Left: Business Setup Form (2/3 width) */}
            <Paper
              elevation={2}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 2,
                p: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.9),
                overflow: 'auto'
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Business Setup & AI Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <InputFormContainerV2
                onAnalysisComplete={handleAnalysisSubmit}
                isSubmitting={isAnalyzing}
                workflowId={workflowId}
                workflow={workflow}
              />
            </Paper>
            
            {/* Right: Queries Preview (fixed 300px width) */}
            <KeywordPreviewPanel 
              clientId={clientId!} 
              workflowId={workflowId!} 
              limit={1000}
            />
          </Box>
        </Box>
      )}

      {/* Results Panel - Show when analyzing or complete (and file upload is done)
          Hide old completed results if most recent module is file upload, but always show if currently analyzing */}
      {(isAnalyzing || (analysisComplete && !mostRecentModuleIsFileUpload)) && fileUploadCompleted && !isInitialLoading && (
        <Paper
          elevation={3}
          sx={{
            p: 4,
            backgroundColor: analysisComplete 
              ? alpha(theme.palette.success.light, 0.05)
              : alpha(theme.palette.background.paper, 0.9),
            border: analysisComplete 
              ? `2px solid ${alpha(theme.palette.success.main, 0.3)}`
              : 'none',
          }}
        >
          {/* Success header - only show when complete */}
          {analysisComplete && (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SuccessIcon sx={{ fontSize: 48, color: 'success.main' }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                    Analysis Complete!
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {testKeywordLimit 
                      ? `Test analysis completed with ${testKeywordLimit} queries (not saved to database)`
                      : 'Topics and subtopics have been generated and saved to database'
                    }
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="outlined"
                onClick={() => {
                  setAnalysisComplete(false);
                  setTestKeywordLimit(null);
                }}
                sx={{ flexShrink: 0 }}
              >
                Re-run Analysis
              </Button>
            </Box>
          )}
          
          {/* AI Response - handles both streaming and completed states */}
          <AIResponseStreaming
            isStreaming={isAnalyzing}
            streamedContent={streamedContent}
            isComplete={analysisComplete}
            showRawByDefault={false}
          />
          
          {/* Action buttons - only show when complete */}
          {analysisComplete && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setAnalysisComplete(false);
                  setTestKeywordLimit(null);
                }}
                size="large"
              >
                Re-run Analysis
              </Button>
              {!testKeywordLimit && (
                <Button
                  variant="contained"
                  color="success"
                  endIcon={<NextIcon />}
                  onClick={handleProceedToNext}
                  size="large"
                >
                  Proceed to Semantic Analysis
                </Button>
              )}
              {testKeywordLimit && (
                <Alert severity="info" sx={{ flex: 1, ml: 2 }}>
                  This was a test run with {testKeywordLimit} queries. Click "Start AI Analysis" to run full analysis and proceed to Semantic Analysis.
                </Alert>
              )}
            </Box>
          )}
        </Paper>
      )}
      
      {/* Old success alert - keeping for backwards compatibility but hidden when using accordion */}
      {analysisComplete && false && (
        <Alert 
          severity="success" 
          icon={<SuccessIcon />}
          sx={{ mt: 3 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Query analysis completed successfully!
          </Typography>
          <Typography variant="body2">
            Topics and subtopics have been generated and saved to Snowflake.
          </Typography>
          <Button
            variant="contained"
            color="success"
            size="small"
            endIcon={<NextIcon />}
            onClick={handleProceedToNext}
            sx={{ mt: 2 }}
          >
            Proceed to Semantic Analysis
          </Button>
        </Alert>
      )}

      {/* Session Expired Dialog */}
      <SessionExpiredDialog
        open={showSessionExpiredDialog}
        onClose={() => setShowSessionExpiredDialog(false)}
      />
    </Box>
  );
};

export default KeywordAnalysisStep;

